import { createContext, useContext, useEffect, useMemo, useState } from 'react'

const STORAGE_KEY = 'nq-theme'
const ThemeContext = createContext(null)

function detectInitialTheme() {
  if (typeof window === 'undefined') return 'light'

  const stored = window.localStorage.getItem(STORAGE_KEY)
  if (stored === 'light' || stored === 'dark') return stored

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(detectInitialTheme)

  useEffect(() => {
    const root = document.documentElement
    root.classList.toggle('dark', theme === 'dark')
    root.dataset.theme = theme
    window.localStorage.setItem(STORAGE_KEY, theme)

    // Sync PWA status bar color with active theme
    document.querySelectorAll('meta[name="theme-color"]').forEach((el) => {
      el.removeAttribute('media')
    })
    const activeTag = document.querySelector('meta[name="theme-color"]')
    if (activeTag) {
      activeTag.content = theme === 'dark' ? '#08101c' : '#4f46e5'
    }
  }, [theme])

  const value = useMemo(() => ({
    theme,
    setTheme,
    toggleTheme: () => setTheme((current) => (current === 'dark' ? 'light' : 'dark')),
  }), [theme])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const context = useContext(ThemeContext)

  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider')
  }

  return context
}
